import React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

interface IntegrationFormProps {
  integrationName: string;
}

// Renders a form for configuring an integration with validation
// @param integrationName - Name of the integration being configured
const IntegrationForm: React.FC<IntegrationFormProps> = ({
  integrationName,
}) => {
  // Initial form values
  const initialValues = {
    googleAccount: '',
    googleCampaign: '',
    googleLeadForm: '',
    autopathAudience: '',
  };

  // Validation schema for the form fields
  const validationSchema = Yup.object({
    googleAccount: Yup.string().required('Google Ads account is required'),
    googleCampaign: Yup.string().required('Campaign is required'),
    googleLeadForm: Yup.string().required('Lead form is required'),
    autopathAudience: Yup.string().required('Audience is required'),
  });

  // Handles form submission by logging values and showing a success toast
  // @param values - The form field values
  const handleSubmit = (values: typeof initialValues) => {
    toast.success(`${integrationName} form saved successfully ✅`);
  };

  return (
    <div className='mt-6 border rounded bg-white shadow-sm p-8'>
      <h3 className='text-[24px] font-bold text-[#3F5265] mb-2'>
        Link a lead form
      </h3>
      <p className='text-[#657584] text-[16px] mb-6'>
        Select your Google Ads account, lead form, and Autopath audience
      </p>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {() => (
          <Form className='space-y-6'>
            {/* Google Account */}
            <div>
              <label className='block text-[12px] font-normal text-[#3F5265] mb-1'>
                GOOGLE AD'S ACCOUNT
              </label>
              <Field
                as='select'
                name='googleAccount'
                className='w-1/2 border rounded px-3 py-3 bg-[#F5F8FA]'
              >
                <option value=''>Select a Google Ads account</option>
                <option value='account1'>Account 1</option>
                <option value='account2'>Account 2</option>
              </Field>
              <ErrorMessage
                name='googleAccount'
                component='div'
                className='text-red-500 text-sm mt-1'
              />
            </div>

            {/* Google Campaign */}
            <div>
              <label className='block text-[12px] font-normal text-[#3F5265] mb-1'>
                GOOGLE AD'S CAMPAIGN
              </label>
              <Field
                as='select'
                name='googleCampaign'
                className='w-1/2 px-3 py-3 border rounded bg-[#F5F8FA]'
              >
                <option value=''>Select a Google Ads campaign</option>
                <option value='campaign1'>Campaign 1</option>
                <option value='campaign2'>Campaign 2</option>
              </Field>
              <ErrorMessage
                name='googleCampaign'
                component='div'
                className='text-red-500 text-sm mt-1'
              />
            </div>

            {/* Google Lead Form */}
            <div>
              <label className='block text-[12px] font-normal text-[#3F5265] mb-1'>
                GOOGLE LEAD FORM
              </label>
              <Field
                as='select'
                name='googleLeadForm'
                className='w-1/2 px-3 py-3 border rounded bg-[#F5F8FA]'
              >
                <option value=''>Select a Google lead form</option>
                <option value='form1'>Form 1</option>
                <option value='form2'>Form 2</option>
              </Field>
              <ErrorMessage
                name='googleLeadForm'
                component='div'
                className='text-red-500 text-sm mt-1'
              />
            </div>

            {/* Autopath Audience */}
            <div>
              <label className='block text-[12px] font-normal text-[#3F5265] mb-1'>
                AUTOPATH AUDIENCE
              </label>
              <Field
                as='select'
                name='autopathAudience'
                className='w-1/2 px-3 py-3 border rounded bg-[#F5F8FA]'
              >
                <option value=''>Select your audience</option>
                <option value='audience1'>Audience 1</option>
                <option value='audience2'>Audience 2</option>
              </Field>
              <ErrorMessage
                name='autopathAudience'
                component='div'
                className='text-red-500 text-sm mt-1'
              />
            </div>

            {/* Save Button */}
            <div className='flex justify-end'>
              <button
                type='submit'
                className='px-6 py-2 bg-[#FF5722] text-white rounded-md font-medium hover:bg-[#e04a1c]'
              >
                Save
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default IntegrationForm;
